/**
 * Composition hook for AI Agent chat.
 * Combines chat state management with message handling (useAiAgentChatMessages).
 */

import { useState, useCallback, useEffect } from "react";
import type { ChatMessage, UsageInfoResponse } from "@/domain/aiAgent";
import { useAiAgentChatMessages } from "./useAiAgentChatMessages";
import { useAiAgentSessionService } from "../useRepositories";

const QUOTA_RESET_TIMEOUT_MS = 5 * 60 * 1000;

export const useAiAgentChat = () => {
	const aiAgentSessionService = useAiAgentSessionService();

	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [sessionId, setSessionId] = useState<string>(() => {
		const stored = aiAgentSessionService.getSessionId();
		if (stored) return stored;

		const newId = crypto.randomUUID();
		aiAgentSessionService.setSessionId(newId);
		return newId;
	});
	const [usageInfo, setUsageInfo] = useState<UsageInfoResponse | null>(null);
	const [quotaExceeded, setQuotaExceeded] = useState(false);

	// Auto-reset quotaExceeded after 5 minutes
	useEffect(() => {
		if (!quotaExceeded) return;
		const timeoutId = setTimeout(() => {
			setQuotaExceeded(false);
		}, QUOTA_RESET_TIMEOUT_MS);
		return () => clearTimeout(timeoutId);
	}, [quotaExceeded]);

	const { sendQuestion, submitFeedback, abortControllerRef } =
		useAiAgentChatMessages({
			sessionId,
			quotaExceeded,
			setMessages,
			setIsLoading,
			setUsageInfo,
			setQuotaExceeded,
		});

	const clearChat = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;

		setMessages([]);
		setIsLoading(false);
		setQuotaExceeded(false);

		const newSessionId = crypto.randomUUID();
		setSessionId(newSessionId);
		aiAgentSessionService.setSessionId(newSessionId);
	}, [abortControllerRef, aiAgentSessionService]);

	return {
		messages,
		isLoading,
		sendQuestion,
		clearChat,
		submitFeedback,
		usageInfo,
		quotaExceeded,
	};
};
